import { CheckboxGroup, GenericCombobox, InputGroup, PageSection } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useMemo } from 'react'
import { NON_EU_COUNTRIES } from '@/common'
import { SupplierFormData } from './SupplierForm.schema'
import EmailInputGroup from '@/shared/components/EmailInputGroup'
import { orderBy } from 'es-toolkit'

type SupplierFormProps = {
  data: SupplierFormData
  onChange: (data: SupplierFormData) => void
}

export default function SupplierForm({ data, onChange }: SupplierFormProps) {
  const { t } = useI18n()

  const {
    isBusinessRegistrationNumberNotKnown,
    supplierName,
    businessRegistrationNumber,
    street,
    streetNumber,
    street2,
    postCode,
    city,
    poBox,
    subDivision,
    country,
    contactName,
    contactPhoneNumber,
    contactEmail
  } = data

  const onIsBusinessRegistrationNumberNotKnownChange = useCallback(
    (val: boolean) => {
      onChange({
        ...data,
        isBusinessRegistrationNumberNotKnown: val,
        businessRegistrationNumber: ''
      })
    },
    [data, onChange]
  )

  const countryOptions = useMemo(() => {
    return orderBy(
      NON_EU_COUNTRIES.map(country => ({
        value: country.value,
        label: t(`common:countriesByIsoAlpha2:${country.value}`)
      })),
      [option => option.label.toLowerCase().normalize('NFKD')],
      ['asc']
    )
  }, [t])

  return (
    <div className="space-y-6 pb-2">
      <PageSection title={t('suppliers:supplierInputFields.sections.suppliersDetails')}>
        <div className="flex gap-4">
          <div className="flex-1">
            <InputGroup
              label={t('suppliers:supplierInputFields.supplierName')}
              required
              value={supplierName || ''}
              onChange={evt => onChange({ ...data, supplierName: evt.target.value })}
            />
          </div>
          <div className="flex-1 space-y-2">
            <InputGroup
              required
              tooltip={t('suppliers:supplierInputFields.businessRegistrationNumber.tooltip')}
              label={t('suppliers:supplierInputFields.businessRegistrationNumber.label')}
              value={businessRegistrationNumber || ''}
              onChange={evt => onChange({ ...data, businessRegistrationNumber: evt.target.value })}
              disabled={isBusinessRegistrationNumberNotKnown}
            />
            <CheckboxGroup
              id="isBusinessRegistrationNumberNotKnown"
              checked={isBusinessRegistrationNumberNotKnown}
              onChange={onIsBusinessRegistrationNumberNotKnownChange}
              label={t('suppliers:supplierInputFields.isBusinessRegistrationNumberNotKnown')}
            />
          </div>
        </div>
      </PageSection>
      <PageSection title={t('suppliers:supplierInputFields.sections.address')}>
        <div className="grid grid-cols-2 gap-4">
          <InputGroup
            id="street"
            label={t('common:inputFields.street.label')}
            value={street}
            onChange={evt => onChange({ ...data, street: evt.target.value })}
          />

          <InputGroup
            id="streetNumber"
            label={t('common:inputFields.streetNumber.label')}
            value={streetNumber}
            onChange={evt => onChange({ ...data, streetNumber: evt.target.value })}
          />
        </div>

        <InputGroup
          id="street2"
          label={t('common:inputFields.street2.label')}
          value={street2}
          onChange={evt => onChange({ ...data, street2: evt.target.value })}
        />

        <div className="grid md:grid-cols-3 gap-4">
          <InputGroup
            id="postCode"
            label={t('common:inputFields.postCode.label')}
            value={postCode}
            onChange={evt => onChange({ ...data, postCode: evt.target.value })}
            required={
              NON_EU_COUNTRIES.find(countryItem => countryItem.value === country)
                ?.isPostCodeRequired
            }
          />
          <InputGroup
            id="city"
            label={t('common:inputFields.city.label')}
            value={city}
            onChange={evt => onChange({ ...data, city: evt.target.value })}
            required
          />
          <InputGroup
            id="poBox"
            label={t('common:inputFields.poBox.label')}
            value={poBox}
            onChange={evt => onChange({ ...data, poBox: evt.target.value })}
          />
        </div>
        <InputGroup
          id="subDivision"
          label={t('common:inputFields.subDivision.label')}
          value={subDivision}
          onChange={evt => onChange({ ...data, subDivision: evt.target.value })}
        />
        <GenericCombobox
          required
          label={t('common:inputFields.country.label')}
          items={countryOptions}
          value={
            country ? { value: country, label: t(`common:countriesByIsoAlpha2:${country}`) } : null
          }
          onChange={evt => onChange({ ...data, country: evt?.value || '' })}
        />
      </PageSection>
      <PageSection title={t('suppliers:supplierInputFields.sections.contactDetails')}>
        <div className="grid sm:grid-cols-3 gap-4">
          <InputGroup
            id="contactName"
            label={t('suppliers:supplierInputFields.contactName')}
            value={contactName}
            required
            onChange={evt => onChange({ ...data, contactName: evt.target.value })}
          />
          <InputGroup
            id="contactPhoneNumber"
            label={t('suppliers:supplierInputFields.contactPhoneNumber')}
            value={contactPhoneNumber}
            required
            onChange={evt => onChange({ ...data, contactPhoneNumber: evt.target.value })}
          />
          <EmailInputGroup
            id="contactEmail"
            label={t('suppliers:supplierInputFields.contactEmail')}
            value={contactEmail}
            required
            onChange={evt => onChange({ ...data, contactEmail: evt.target.value })}
          />
        </div>
      </PageSection>
    </div>
  )
}
